import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../services/api';
import pedirSenhaAdmin from '../components/common/pedirSenhaAdmin';
import { hojeBR } from '../utils/data';
import s from './Financeiro.module.css';

export default function BaixaCartoes() {
  const [bandeiras, setBandeiras]       = useState([]);
  const [contas, setContas]             = useState([]);
  const [pendentes, setPendentes]       = useState([]);
  const [selecionados, setSelecionados] = useState(new Set());
  const [focusedIdx, setFocusedIdx]     = useState(0);
  const [filtroBandeira, setFiltroBandeira] = useState('');
  const [contaDestino, setContaDestino] = useState('');
  const [dataBaixa, setDataBaixa]       = useState(hojeBR());
  const [observacoes, setObservacoes]   = useState('');
  const [baixando, setBaixando]         = useState(false);
  const [carregando, setCarregando]     = useState(false);
  const [erro, setErro]                 = useState('');
  const [sucesso, setSucesso]           = useState('');
  const [historico, setHistorico]       = useState([]);
  const [aba, setAba]                   = useState('pendentes');
  const [editandoBandeira, setEditandoBandeira] = useState(null); // pagamento_id sendo editado

  // refs para navegação por teclado sem stale closure
  const focusedIdxRef     = useRef(0);
  const pendentesRef      = useRef([]);
  const selecionadosRef   = useRef(new Set());
  const abaRef            = useRef('pendentes');
  const rowRefs           = useRef({});

  const syncFocused = (idx) => {
    focusedIdxRef.current = idx;
    setFocusedIdx(idx);
    // auto-scroll para a linha focada
    const el = rowRefs.current[idx];
    if (el) el.scrollIntoView({ block: 'nearest' });
  };

  const syncSelecionados = (next) => {
    selecionadosRef.current = next;
    setSelecionados(next);
  };

  const carregar = useCallback(async () => {
    try {
      const [rb, rc, rh] = await Promise.all([
        api.get('/financeiro/bandeiras'),
        api.get('/financeiro/contas'),
        api.get('/financeiro/baixas-cartao'),
      ]);
      setBandeiras(rb.data.filter(b => b.ativo));
      setContas(rc.data.filter(c => c.ativo));
      setHistorico(rh.data);
    } catch { setErro('Erro ao carregar dados.'); }
  }, []);

  const carregarPendentes = useCallback(async () => {
    setCarregando(true);
    try {
      const url = filtroBandeira
        ? `/financeiro/baixas-cartao/pendentes?bandeira_id=${filtroBandeira}`
        : '/financeiro/baixas-cartao/pendentes';
      const { data } = await api.get(url);
      pendentesRef.current = data;
      setPendentes(data);
      // seleciona todos por padrão
      const todos = new Set(data.map(p => p.id));
      syncSelecionados(todos);
      syncFocused(0);
    } catch { setErro('Erro ao carregar pagamentos pendentes.'); }
    finally { setCarregando(false); }
  }, [filtroBandeira]);

  useEffect(() => { carregar(); }, [carregar]);
  useEffect(() => { if (aba === 'pendentes') carregarPendentes(); }, [aba, carregarPendentes]);
  useEffect(() => { abaRef.current = aba; }, [aba]);

  // ── Navegação por teclado ─────────────────────────────────────
  useEffect(() => {
    const handler = (e) => {
      if (abaRef.current !== 'pendentes') return;
      const lista = pendentesRef.current;
      const idx   = focusedIdxRef.current;

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        syncFocused(Math.min(idx + 1, lista.length - 1));
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        syncFocused(Math.max(idx - 1, 0));
        return;
      }
      if (e.key === 'F12') {
        e.preventDefault();
        const item = lista[idx];
        if (!item) return;
        const next = new Set(selecionadosRef.current);
        next.has(item.id) ? next.delete(item.id) : next.add(item.id);
        syncSelecionados(next);
        // avança para o próximo
        if (idx < lista.length - 1) syncFocused(idx + 1);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []); // sem dependências — usa refs para valores atuais

  const pendentesVisiveis = filtroBandeira
    ? pendentes.filter(p => String(p.bandeira_id) === filtroBandeira)
    : pendentes;

  useEffect(() => {
    pendentesRef.current = pendentesVisiveis;
  });

  const toggleSel = (id) => {
    const next = new Set(selecionadosRef.current);
    next.has(id) ? next.delete(id) : next.add(id);
    syncSelecionados(next);
  };

  const toggleTodos = () => {
    if (selecionadosRef.current.size === pendentesVisiveis.length && pendentesVisiveis.length > 0)
      syncSelecionados(new Set());
    else
      syncSelecionados(new Set(pendentesVisiveis.map(p => p.id)));
  };

  const itensSel   = pendentesVisiveis.filter(p => selecionados.has(p.id));
  const totalBruto = itensSel.reduce((acc, p) => acc + parseFloat(p.valor), 0);
  const totalTaxas = itensSel.reduce((acc, p) =>
    acc + parseFloat(p.valor) * parseFloat(p.taxa_percentual) / 100, 0);
  const totalLiq   = totalBruto - totalTaxas;

  // Resumo por bandeira da seleção
  const gruposBandeira = itensSel.reduce((acc, p) => {
    const key  = p.bandeira_id || 'sem';
    const nome = p.bandeira_nome || 'Sem bandeira';
    if (!acc[key]) acc[key] = { nome, qtd: 0, bruto: 0, taxas: 0 };
    const taxa_r = parseFloat(p.valor) * parseFloat(p.taxa_percentual) / 100;
    acc[key].qtd++;
    acc[key].bruto += parseFloat(p.valor);
    acc[key].taxas += taxa_r;
    return acc;
  }, {});

  const fmtBRL = v => parseFloat(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const fmtDT  = d => new Date(d).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
  const fmtD   = d => d ? new Date(String(d).slice(0, 10) + 'T12:00').toLocaleDateString('pt-BR') : '—';

  const baixar = async () => {
    if (itensSel.length === 0) { setErro('Selecione ao menos um pagamento.'); return; }
    setErro(''); setBaixando(true);
    try {
      const { data } = await api.post('/financeiro/baixas-cartao', {
        conta_destino_id: contaDestino ? parseInt(contaDestino) : null,
        data_baixa:       dataBaixa,
        observacoes:      observacoes || null,
        pagamento_ids:    [...selecionados],
      });
      setSucesso(`${itensSel.length} pagamento(s) baixado(s) em ${data.total} lote(s).`);
      setTimeout(() => setSucesso(''), 4000);
      await Promise.all([carregarPendentes(), carregar()]);
    } catch (err) {
      setErro(err.response?.data?.erro || 'Erro ao realizar baixa.');
    } finally { setBaixando(false); }
  };

  const salvarBandeira = async (pgtoId, bandeira_id) => {
    try {
      await api.patch(`/financeiro/pagamentos/${pgtoId}/bandeira`, {
        bandeira_id: bandeira_id ? parseInt(bandeira_id) : null,
      });
      // Atualiza localmente para refletir nova taxa sem recarregar tudo
      await carregarPendentes();
    } catch {
      setErro('Erro ao atualizar bandeira.');
    }
    setEditandoBandeira(null);
  };

  const estornarPendente = async (p) => {
    if (!window.confirm(`Estornar o recebimento em cartão da conta a receber #${p.conta_receber_id}?\n\nA pendência será removida e a conta ficará livre para receber por qualquer forma de pagamento.`)) return;
    const senha = await pedirSenhaAdmin();
    if (senha === null) return;
    setErro('');
    try {
      await api.delete(`/financeiro/baixas-cartao/pendentes/${p.id}`, { data: { senha_admin: senha } });
      setSucesso(`Recebimento estornado — conta a receber #${p.conta_receber_id} liberada.`);
      setTimeout(() => setSucesso(''), 4000);
      await carregarPendentes();
    } catch (err) {
      setErro(err.response?.data?.erro || 'Erro ao estornar pendência.');
    }
  };

  const cancelarBaixa = async (bx) => {
    if (!window.confirm(`Cancelar a baixa #${bx.id}?\n\nOs pagamentos voltam para a lista de pendentes e a movimentação financeira e as taxas lançadas no DRE são estornadas.`)) return;
    const senha = await pedirSenhaAdmin();
    if (senha === null) return;
    setErro('');
    try {
      await api.post(`/financeiro/baixas-cartao/${bx.id}/cancelar`, { senha_admin: senha });
      setSucesso(`Baixa #${bx.id} cancelada — pagamentos liberados para nova baixa.`);
      setTimeout(() => setSucesso(''), 4000);
      await Promise.all([carregar(), carregarPendentes()]);
    } catch (err) {
      setErro(err.response?.data?.erro || 'Erro ao cancelar baixa.');
    }
  };

  const gerarArquivo = (bx) => {
    const txt = [
      `BAIXA DE CARTÃO${bx.bandeira_nome ? ` — ${bx.bandeira_nome}` : ''}`,
      `Data: ${fmtD(bx.data_baixa)}`,
      `Total Bruto:   R$ ${fmtBRL(bx.total_bruto)}`,
      `Total Taxas:   R$ ${fmtBRL(bx.total_taxas)}`,
      `Total Líquido: R$ ${fmtBRL(bx.total_liquido)}`,
      `Conta Destino: ${bx.conta_nome || '—'}`,
    ].join('\r\n');
    const a = Object.assign(document.createElement('a'), {
      href: URL.createObjectURL(new Blob([txt], { type: 'text/plain;charset=utf-8' })),
      download: `baixa_${bx.bandeira_nome || 'sem-bandeira'}_${String(bx.data_baixa).slice(0, 10)}.txt`,
    });
    a.click(); URL.revokeObjectURL(a.href);
  };

  const gerarLote = () => {
    const linhas = historico.map(bx =>
      `${fmtD(bx.data_baixa)} | ${(bx.bandeira_nome || 'Sem bandeira').padEnd(16)} | Bruto: R$${fmtBRL(bx.total_bruto)} | Taxas: R$${fmtBRL(bx.total_taxas)} | Líq.: R$${fmtBRL(bx.total_liquido)} | ${bx.conta_nome || '—'}`
    ).join('\r\n');
    const a = Object.assign(document.createElement('a'), {
      href: URL.createObjectURL(new Blob(
        [`LOTE DE BAIXAS DE CARTÃO\r\n${'-'.repeat(110)}\r\n${linhas}`],
        { type: 'text/plain;charset=utf-8' }
      )),
      download: `lote_baixas_${hojeBR()}.txt`,
    });
    a.click(); URL.revokeObjectURL(a.href);
  };

  // totais do rodapé
  const totalGeralBruto = pendentesVisiveis.reduce((a, p) => a + parseFloat(p.valor), 0);
  const totalGeralTaxas = pendentesVisiveis.reduce((a, p) =>
    a + parseFloat(p.valor) * parseFloat(p.taxa_percentual) / 100, 0);
  const totalGeralLiq   = totalGeralBruto - totalGeralTaxas;

  return (
    <div className={s.erpWrap}>
      <div className={s.titleBar}><span>💳</span> Baixa de Cartões</div>

      <div className={s.toolbar}>
        <button className={`${s.btnErp} ${aba === 'pendentes' ? s.btnErpPrimario : ''}`}
          onClick={() => setAba('pendentes')}>Pendentes</button>
        <button className={`${s.btnErp} ${aba === 'historico' ? s.btnErpPrimario : ''}`}
          onClick={() => setAba('historico')}>Histórico de Baixas</button>
        {aba === 'historico' && (
          <button className={s.btnErp} onClick={gerarLote} style={{ marginLeft: 'auto' }}>
            ⬇ Gerar Lote
          </button>
        )}
        {aba === 'pendentes' && (
          <span style={{ marginLeft: 'auto', fontSize: 11, color: '#555', alignSelf: 'center' }}>
            ↑↓ navegar &nbsp;|&nbsp; F12 marcar/desmarcar e avançar
          </span>
        )}
      </div>

      {erro    && <div className={s.erroMsg}>{erro}</div>}
      {sucesso && <div className={s.sucessoMsg}>{sucesso}</div>}

      {aba === 'pendentes' && (
        <div className={s.conteudoSplit}>
          {/* Painel lateral */}
          <div className={`${s.painel} ${s.painelLeft}`} style={{ width: 280 }}>
            <div className={s.painelScroll}>
              <div style={{ padding: 10 }}>

                <div className={s.fieldset}>
                  <div className={s.legend}>Filtro por Bandeira</div>
                  <select className={`${s.select} ${s.inputLarge}`} value={filtroBandeira}
                    onChange={e => setFiltroBandeira(e.target.value)}>
                    <option value="">— todas —</option>
                    {bandeiras.map(b => <option key={b.id} value={b.id}>{b.nome}</option>)}
                  </select>
                </div>

                <div className={s.fieldset}>
                  <div className={s.legend}>Configurações da Baixa</div>
                  <div className={s.formGrupo} style={{ marginBottom: 7 }}>
                    <label className={s.label}>Conta Banco Destino</label>
                    <select className={`${s.select} ${s.inputLarge}`} value={contaDestino}
                      onChange={e => setContaDestino(e.target.value)}>
                      <option value="">— nenhuma —</option>
                      {contas.map(c => <option key={c.id} value={c.id}>{c.nome}</option>)}
                    </select>
                  </div>
                  <div className={s.formGrupo} style={{ marginBottom: 7 }}>
                    <label className={s.label}>Data da Baixa</label>
                    <input className={`${s.input} ${s.inputLarge}`} type="date"
                      value={dataBaixa} onChange={e => setDataBaixa(e.target.value)} />
                  </div>
                  <div className={s.formGrupo}>
                    <label className={s.label}>Observações</label>
                    <input className={`${s.input} ${s.inputLarge}`} value={observacoes}
                      onChange={e => setObservacoes(e.target.value)} placeholder="Opcional..." />
                  </div>
                </div>

                {/* Resumo por bandeira */}
                {itensSel.length > 0 && (
                  <div className={s.fieldset}>
                    <div className={s.legend}>Resumo da Seleção</div>
                    <div style={{ fontSize: 12 }}>
                      {Object.values(gruposBandeira).map((g, i) => (
                        <div key={i} style={{ borderBottom: '1px solid #eee', padding: '3px 0' }}>
                          <div style={{ fontWeight: 'bold' }}>{g.nome} — {g.qtd}x</div>
                          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <span style={{ color: '#555' }}>Bruto:</span>
                            <span>R$ {fmtBRL(g.bruto)}</span>
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <span style={{ color: '#b91c1c' }}>Taxas:</span>
                            <span style={{ color: '#b91c1c' }}>R$ {fmtBRL(g.taxas)}</span>
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <span style={{ color: '#065f46' }}>Líq.:</span>
                            <span style={{ color: '#065f46', fontWeight: 'bold' }}>
                              R$ {fmtBRL(g.bruto - g.taxas)}
                            </span>
                          </div>
                        </div>
                      ))}
                      <div style={{ marginTop: 6, borderTop: '2px solid #c5d5e8', paddingTop: 4 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                          <span>Total bruto:</span><strong>R$ {fmtBRL(totalBruto)}</strong>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', color: '#b91c1c' }}>
                          <span>Total taxas:</span><strong>R$ {fmtBRL(totalTaxas)}</strong>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', color: '#065f46', fontSize: 13 }}>
                          <span>Total líquido:</span><strong>R$ {fmtBRL(totalLiq)}</strong>
                        </div>
                      </div>
                    </div>
                    <button className={s.btnErpPrimario} onClick={baixar} disabled={baixando}
                      style={{ width: '100%', marginTop: 10 }}>
                      {baixando ? 'Baixando...' : `✔ Baixar ${itensSel.length} pgto(s)`}
                    </button>
                  </div>
                )}

                {itensSel.length === 0 && pendentesVisiveis.length > 0 && (
                  <div style={{ fontSize: 11, color: '#888', textAlign: 'center', padding: '8px 0' }}>
                    Use ↑↓ para navegar e F12<br/>para marcar/desmarcar.
                  </div>
                )}

              </div>
            </div>
          </div>

          {/* Tabela */}
          <div className={s.painelRight} style={{ padding: 0, overflow: 'auto' }}>
            {carregando
              ? <div style={{ padding: 20, color: '#888', fontSize: 12 }}>Carregando...</div>
              : pendentesVisiveis.length === 0
                ? <div style={{ padding: 20, color: '#888', fontSize: 12, textAlign: 'center' }}>
                    Nenhum pagamento de cartão pendente de baixa.
                  </div>
                : (
                  <table className={s.tabela} style={{ tableLayout: 'fixed' }}>
                    <colgroup>
                      <col style={{ width: 36 }} />
                      <col style={{ width: 125 }} />
                      <col style={{ width: 110 }} />
                      <col style={{ width: 115 }} />
                      <col style={{ width: 100 }} />
                      <col style={{ width: 65 }} />
                      <col style={{ width: 85 }} />
                      <col style={{ width: 60 }} />
                      <col style={{ width: 80 }} />
                      <col style={{ width: 85 }} />
                    </colgroup>
                    <thead>
                      <tr>
                        <th>
                          <input type="checkbox" className={s.check}
                            checked={selecionados.size === pendentesVisiveis.length && pendentesVisiveis.length > 0}
                            onChange={toggleTodos} title="Marcar/desmarcar todos" />
                        </th>
                        <th>Origem</th>
                        <th>Data/Hora</th>
                        <th>Forma</th>
                        <th>Bandeira</th>
                        <th style={{ textAlign: 'center' }}>Parcelas</th>
                        <th style={{ textAlign: 'right' }}>Valor Bruto</th>
                        <th style={{ textAlign: 'right' }}>Taxa %</th>
                        <th style={{ textAlign: 'right' }}>Taxa R$</th>
                        <th style={{ textAlign: 'right' }}>Líquido</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pendentesVisiveis.map((p, idx) => {
                        const taxa_r  = parseFloat(p.valor) * parseFloat(p.taxa_percentual) / 100;
                        const liq     = parseFloat(p.valor) - taxa_r;
                        const isSel   = selecionados.has(p.id);
                        const isFocus = focusedIdx === idx;
                        return (
                          <tr
                            key={p.id}
                            ref={el => { rowRefs.current[idx] = el; }}
                            style={{ cursor: 'pointer',
                              outline: isFocus ? '2px solid #316ac5' : 'none',
                              outlineOffset: '-2px' }}
                            className={isSel ? s.trSelecionada : ''}
                            onClick={() => { syncFocused(idx); toggleSel(p.id); }}
                          >
                            <td onClick={e => e.stopPropagation()}>
                              <input type="checkbox" className={s.check}
                                checked={isSel} onChange={() => toggleSel(p.id)} />
                            </td>
                            <td onClick={e => p.conta_receber_id && e.stopPropagation()} style={{ whiteSpace: 'nowrap' }}>
                              {p.conta_receber_id ? (
                                <>
                                  <span title={p.venda_id ? `Conta a receber #${p.conta_receber_id} (venda #${p.venda_id})` : `Conta a receber #${p.conta_receber_id}`}>
                                    CR {p.conta_receber_id}{p.venda_id ? ` (#${p.venda_id})` : ''}
                                  </span>
                                  <button className={s.btnErp}
                                    style={{ minWidth: 0, height: 18, fontSize: 10, padding: '0 4px', marginLeft: 4, color: '#b91c1c' }}
                                    title="Estornar recebimento em cartão (a conta a receber volta para pendente)"
                                    onClick={() => estornarPendente(p)}>↩</button>
                                </>
                              ) : p.venda_id ? `#${p.venda_id}` : '—'}
                            </td>
                            <td style={{ fontSize: 11 }}>{fmtDT(p.venda_em)}</td>
                            <td style={{ fontSize: 11 }}>{p.forma_pagamento}</td>
                            <td style={{ fontSize: 11, padding: '1px 4px' }}
                              onClick={e => { e.stopPropagation(); setEditandoBandeira(p.id); }}>
                              {editandoBandeira === p.id
                                ? (
                                  <select
                                    autoFocus
                                    defaultValue={p.bandeira_id || ''}
                                    style={{ fontSize: 11, height: 22, width: '100%',
                                      border: '1px solid #316ac5', borderRadius: 2 }}
                                    onChange={e => salvarBandeira(p.id, e.target.value)}
                                    onBlur={e => salvarBandeira(p.id, e.target.value)}
                                    onClick={e => e.stopPropagation()}>
                                    <option value="">— nenhuma —</option>
                                    {bandeiras.map(b =>
                                      <option key={b.id} value={b.id}>{b.nome}</option>
                                    )}
                                  </select>
                                )
                                : (
                                  <span style={{ cursor: 'pointer',
                                    borderBottom: '1px dashed #aaa',
                                    color: p.bandeira_nome ? undefined : '#bbb' }}
                                    title="Clique para corrigir bandeira">
                                    {p.bandeira_nome || '— corrigir'}
                                  </span>
                                )
                              }
                            </td>
                            <td style={{ textAlign: 'center' }}>{p.parcelas || 1}x</td>
                            <td style={{ textAlign: 'right' }}>{fmtBRL(p.valor)}</td>
                            <td style={{ textAlign: 'right' }}>
                              {parseFloat(p.taxa_percentual || 0).toFixed(2)}%
                            </td>
                            <td style={{ textAlign: 'right', color: taxa_r > 0 ? '#b91c1c' : '#aaa' }}>
                              {fmtBRL(taxa_r)}
                            </td>
                            <td style={{ textAlign: 'right', fontWeight: 'bold',
                              color: isSel ? undefined : '#065f46' }}>
                              {fmtBRL(liq)}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot>
                      <tr style={{ background: '#dde6f0', fontWeight: 'bold', fontSize: 12 }}>
                        <td colSpan={6} style={{ padding: '4px 8px' }}>
                          Total geral ({pendentesVisiveis.length} pagamentos)
                          {itensSel.length > 0 && itensSel.length !== pendentesVisiveis.length &&
                            ` — ${itensSel.length} selecionados`}
                        </td>
                        <td style={{ textAlign: 'right', padding: '4px 8px' }}>{fmtBRL(totalGeralBruto)}</td>
                        <td></td>
                        <td style={{ textAlign: 'right', padding: '4px 8px', color: '#b91c1c' }}>
                          {fmtBRL(totalGeralTaxas)}
                        </td>
                        <td style={{ textAlign: 'right', padding: '4px 8px', color: '#065f46' }}>
                          {fmtBRL(totalGeralLiq)}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                )
            }
          </div>
        </div>
      )}

      {aba === 'historico' && (
        <div className={s.conteudo}>
          {historico.length === 0
            ? <div style={{ color: '#888', fontSize: 12 }}>Nenhuma baixa realizada ainda.</div>
            : (
              <table className={s.tabela}>
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Data</th>
                    <th>Bandeira</th>
                    <th>Conta Destino</th>
                    <th style={{ textAlign: 'right' }}>Total Bruto</th>
                    <th style={{ textAlign: 'right' }}>Total Taxas</th>
                    <th style={{ textAlign: 'right' }}>Total Líquido</th>
                    <th>Usuário</th>
                    <th>Status</th>
                    <th>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {historico.map(bx => (
                    <tr key={bx.id} style={bx.cancelada ? { opacity: 0.55 } : undefined}>
                      <td>{bx.id}</td>
                      <td>{fmtD(bx.data_baixa)}</td>
                      <td>{bx.bandeira_nome || '—'}</td>
                      <td>{bx.conta_nome || '—'}</td>
                      <td style={{ textAlign: 'right' }}>{fmtBRL(bx.total_bruto)}</td>
                      <td style={{ textAlign: 'right', color: '#b91c1c' }}>{fmtBRL(bx.total_taxas)}</td>
                      <td style={{ textAlign: 'right', fontWeight: 'bold', color: '#065f46' }}>
                        {fmtBRL(bx.total_liquido)}
                      </td>
                      <td>{bx.usuario_nome || '—'}</td>
                      <td>
                        {bx.cancelada
                          ? <span className={`${s.badge} ${s.badgeInativo}`}>Cancelada</span>
                          : <span className={`${s.badge} ${s.badgeAtivo}`}>Efetivada</span>}
                        {bx.automatica && (
                          <span title="Fechada sozinha pela venda, por regra fixa de prazo (Parâmetros → PDV → Fechamento de Cartão) — não passou pela conferência manual desta tela."
                            style={{ marginLeft: 4, fontSize: 10, color: '#6b7280' }}>🤖 auto</span>
                        )}
                      </td>
                      <td style={{ whiteSpace: 'nowrap' }}>
                        <button className={s.btnErp}
                          style={{ height: 20, fontSize: 11, padding: '0 6px' }}
                          title="Baixar arquivo"
                          onClick={() => gerarArquivo(bx)}>⬇</button>
                        {!bx.cancelada && (
                          <button className={s.btnErp}
                            style={{ height: 20, fontSize: 11, padding: '0 6px', marginLeft: 4, color: '#b91c1c' }}
                            title="Cancelar baixa para refazer"
                            onClick={() => cancelarBaixa(bx)}>✕ Cancelar</button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )
          }
        </div>
      )}
    </div>
  );
}
